import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, ClipboardList, FilePenLine, Calendar, Wallet, ShieldCheck, MessageCircle } from "lucide-react";

const KIND_META = {
  intake:      { label: "New intake",     icon: ClipboardList, to: "/admin/intake" },
  amendment:   { label: "New amendment",  icon: FilePenLine,   to: "/admin/amendments" },
  appointment: { label: "New booking",    icon: Calendar,      to: "/admin/appointments" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
};

/**
 * AdminNotificationBadge — bell icon + unread pill that lives in the
 * admin sidebar. Polls /api/admin/notifications/summary every 30s.
 * Click opens a dropdown with counts by queue + the 5 most recent items.
 */
export default function AdminNotificationBadge() {
  const [data, setData] = useState({ counts: {}, recent: [] });
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/admin/notifications/summary")
      .then(r => { if (mounted) setData(r.data || { counts: {}, recent: [] }); })
      .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const total = data.counts?.total_new || 0;
  const rows = [
    { key: "intakes_new",       label: "Intakes",            icon: ClipboardList, to: "/admin/intake"      },
    { key: "amendments_new",    label: "Amendments",         icon: FilePenLine,   to: "/admin/amendments"  },
    { key: "appointments_upcoming", label: "Upcoming bookings", icon: Calendar,   to: "/admin/appointments"},
    { key: "cash_app_pending",  label: "Cash App payments",  icon: Wallet,        to: "/admin/payments"    },
    { key: "affiliate_pending", label: "Affiliate reviews",  icon: ShieldCheck,   to: "/admin/affiliate-contracts" },
    { key: "questions_open",    label: "Open questions",     icon: MessageCircle, to: "/questions"         },
  ];

  return (
    <div className="relative" ref={wrap}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative flex items-center gap-2 w-full px-4 py-3 rounded-md text-sm text-foreground hover:bg-secondary transition-colors"
        data-testid="admin-notification-bell"
      >
        <Bell size={16} />
        <span className="flex-1 text-left">Activity</span>
        {total > 0 && (
          <span
            className="min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center"
            data-testid="admin-notification-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute z-50 left-full ml-2 top-0 w-80 rounded-lg border border-border bg-card shadow-xl p-3 space-y-3"
          data-testid="admin-notification-dropdown"
        >
          <div className="flex items-center justify-between px-1">
            <div className="label-caps text-[10px]">Your queue</div>
            {total > 0 && (
              <span className="text-[10px] text-muted-foreground">{total} new</span>
            )}
          </div>

          {/* Counts grid */}
          <div className="grid grid-cols-2 gap-1.5">
            {rows.map(r => {
              const n = data.counts?.[r.key] || 0;
              const Icon = r.icon;
              return (
                <Link
                  key={r.key}
                  to={r.to}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-2 rounded-md p-2 border text-xs transition-colors ${
                    n > 0 ? "border-primary/40 bg-primary/5 hover:bg-primary/10" : "border-border bg-background hover:bg-secondary"
                  }`}
                  data-testid={`admin-notif-count-${r.key}`}
                >
                  <Icon size={13} className="text-primary" />
                  <span className="flex-1 truncate">{r.label}</span>
                  <span className={`font-bold ${n > 0 ? "text-foreground" : "text-muted-foreground"}`}>{n}</span>
                </Link>
              );
            })}
          </div>

          {/* Recent list */}
          {data.recent?.length > 0 && (
            <div>
              <div className="label-caps text-[10px] mb-1 px-1">Latest</div>
              <ul className="space-y-1">
                {data.recent.map((r) => {
                  const meta = KIND_META[r.kind] || KIND_META.intake;
                  const Icon = meta.icon;
                  return (
                    <li key={`${r.kind}-${r.id}`}>
                      <Link
                        to={meta.to}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 rounded-md p-2 hover:bg-secondary text-xs"
                        data-testid={`admin-notif-recent-${r.kind}`}
                      >
                        <Icon size={12} className="text-primary shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate">
                            <span className="font-medium">{meta.label}</span>
                            {r.name ? ` · ${r.name}` : r.email ? ` · ${r.email}` : ""}
                            {r.tax_year ? ` · TY${r.tax_year}` : ""}
                          </div>
                          {r.referrer_ref && (
                            <div className="text-[10px] text-primary truncate">via {r.referrer_ref}</div>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground shrink-0">{timeAgo(r.when)}</div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {total === 0 && data.recent?.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-3">
              All caught up. New submissions land here automatically.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
